import { Component, type ReactNode } from 'react';

/**
 * App-level error boundary. Without this, ANY uncaught error thrown during
 * render tears down the whole React tree and the user is left staring at a
 * blank screen with no way to recover and no surfaced error — which is
 * exactly the "the memo is shown but couldn't actually load" failure mode.
 *
 * This catches render errors, shows a calm recoverable fallback (reload /
 * go home), and re-logs the error to the console so the platform's automatic
 * error reporting captures it for debugging. It does NOT swallow the error
 * silently — visibility is the whole point.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Surface to the console so the platform's error telemetry records it.
    // (The SDK auto-reports uncaught errors; a render error caught by a
    // boundary must be re-logged here to still be reported.)
    console.error('Render error caught by ErrorBoundary:', error, info);
  }

  handleReload = () => {
    // Clearing the error and reloading gives the cleanest recovery — the
    // data fetch re-runs from scratch.
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'var(--surface-page, #f4efe2)',
          }}
        >
          <div
            style={{
              maxWidth: 460,
              textAlign: 'center',
              background: 'var(--surface-card, #fbf8f0)',
              border: '1px solid var(--border-hairline, rgba(15,23,20,0.1))',
              borderRadius: 16,
              padding: '40px 32px',
              boxShadow:
                '0 1px 2px rgba(15,23,20,0.04), 0 18px 50px rgba(15,23,20,0.10)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--f-mono, monospace)',
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--brass-deep, #a37828)',
                fontWeight: 700,
                marginBottom: 14,
              }}
            >
              Something went wrong
            </div>
            <h1
              style={{
                fontFamily: 'var(--f-serif, serif)',
                fontWeight: 400,
                fontSize: 24,
                lineHeight: 1.25,
                color: 'var(--ink, #0f1714)',
                margin: '0 0 12px',
              }}
            >
              This page didn&apos;t load correctly.
            </h1>
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.6,
                color: 'var(--text-decorative, #6b6b6b)',
                margin: '0 0 24px',
              }}
            >
              Your work is safe — nothing was lost. This is a display issue on
              our side. Reloading usually fixes it.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={this.handleReload}
                style={{
                  fontFamily: 'var(--f-sans, inherit)',
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--parchment, #f4efe2)',
                  background: 'var(--heartwood, #1b3a2e)',
                  border: 'none',
                  borderRadius: 999,
                  padding: '12px 24px',
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                Reload the page
              </button>
              <a
                href="/dashboard"
                style={{
                  fontFamily: 'var(--f-sans, inherit)',
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--ink, #0f1714)',
                  background: 'transparent',
                  border: '1px solid var(--border-stronger, rgba(15,23,20,0.18))',
                  borderRadius: 999,
                  padding: '12px 24px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 44,
                  boxSizing: 'border-box',
                }}
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
